import { useEffect, useState } from 'react';
import { get } from '../../../services/api';
import { useLang, type Lang } from '../../../i18n';
import type { OfficeLocation, FaqItem } from '../../../types';

const LANG_MAP: Record<Lang, string> = {
  'uz-Latn': 'uz_lat',
  'uz-Cyrl': 'uz_cyr',
  ru: 'ru',
  en: 'en',
};

export interface ContactsData {
  offices: OfficeLocation[];
  faq: FaqItem[];
}

interface ListResp<T> {
  items?: T[];
  results?: T[];
}

function pickList<T>(resp: ListResp<T> | T[] | null | undefined): T[] {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.items)) return resp.items;
  if (Array.isArray(resp.results)) return resp.results;
  return [];
}

export function useContactsData() {
  const { lang } = useLang();
  const apiLang = LANG_MAP[lang];
  const [data, setData] = useState<ContactsData>({ offices: [], faq: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    Promise.allSettled([
      get<ListResp<OfficeLocation>>(`/about/offices/?lang=${apiLang}`),
      get<ListResp<FaqItem>>(`/faq/?lang=${apiLang}`),
    ]).then((results) => {
      if (cancel) return;
      const [offices, faq] = results;
      const anyOk = results.some((r) => r.status === 'fulfilled');
      setError(!anyOk);
      setData({
        offices: offices.status === 'fulfilled' ? pickList(offices.value) : [],
        faq: faq.status === 'fulfilled' ? pickList(faq.value) : [],
      });
      setLoading(false);
    });
    return () => {
      cancel = true;
    };
  }, [apiLang]);

  return { data, loading, error };
}

export function pickPrimaryOffice(offices: OfficeLocation[]): OfficeLocation | null {
  if (offices.length === 0) return null;
  return offices.find((o) => o.is_primary) || offices[0];
}
